import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, FileText, Download, RefreshCw } from 'lucide-react'
import { employeeApi, getEmployeeToken } from '../../lib/employeeApi'
import { Capacitor } from '@capacitor/core'

interface PaySlip {
  id: string; name: string; fileName: string; period: string | null; createdAt: string; fileSize: number
}

export default function MobilePaySlips() {
  const navigate = useNavigate()
  const [payslips, setPayslips] = useState<PaySlip[]>([])
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const r = await employeeApi.get('/employee/me/payslips')
      setPayslips(r.data || [])
    } catch { } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const open = async (ps: PaySlip) => {
    setDownloading(ps.id)
    try {
      // Sur APK Android : ouvrir via URL directe avec token dans le header n'est pas possible
      // → télécharger le blob et l'ouvrir via Capacitor Browser ou lien téléchargement
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser')
        const baseURL = import.meta.env.VITE_API_URL || ''
        const token = getEmployeeToken()
        // Construire l'URL avec token en query param pour contourner le WebView
        await Browser.open({
          url: `${baseURL}/api/employee/me/payslips/${ps.id}?token=${token}`,
          presentationStyle: 'popover'
        })
      } else {
        const win = window.open('', '_blank')
        const r = await employeeApi.get(`/employee/me/payslips/${ps.id}`, { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
        if (win) { win.location.href = url } else {
          const a = document.createElement('a')
          a.href = url; a.download = ps.fileName || `bulletin-${ps.period || ps.id}.pdf`
          document.body.appendChild(a); a.click(); document.body.removeChild(a)
        }
      }
    } catch {
      alert('Impossible d\'ouvrir ce document')
    } finally { setDownloading(null) }
  }

  const periodLabel = (ps: PaySlip) => {
    if (ps.period) {
      const [y, m] = ps.period.split('-')
      if (y && m) return new Date(Number(y), Number(m) - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    }
    return format(new Date(ps.createdAt), 'MMMM yyyy', { locale: fr })
  }

  const fileSize = (bytes: number) => bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} Mo`
    : `${Math.round(bytes / 1024)} Ko`

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/home')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <FileText size={18} className="text-blue-600" /> Mes bulletins de paie
          </h1>
          <p className="text-xs text-gray-500">{payslips.length} document(s)</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 px-4 py-4">
        {loading ? (
          <div className="text-center py-16">
            <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-blue-500" />
            <p className="text-sm text-gray-400">Chargement...</p>
          </div>
        ) : payslips.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FileText size={56} className="mx-auto mb-3 text-gray-200" />
            <p className="font-medium">Aucun bulletin disponible</p>
            <p className="text-sm mt-1">Vos bulletins de paie apparaîtront ici</p>
          </div>
        ) : (
          <div className="space-y-2">
            {payslips.map(ps => (
              <button key={ps.id} onClick={() => open(ps)}
                disabled={downloading === ps.id}
                className="w-full bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 text-left hover:border-blue-300 transition-colors disabled:opacity-60">
                <div className="w-12 h-14 bg-red-50 border border-red-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  {downloading === ps.id
                    ? <RefreshCw size={20} className="text-red-400 animate-spin" />
                    : <FileText size={20} className="text-red-500" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 capitalize">{periodLabel(ps)}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{ps.name}</p>
                  {ps.fileSize > 0 && <p className="text-xs text-gray-400 mt-0.5">{fileSize(ps.fileSize)}</p>}
                </div>
                <Download size={18} className="text-blue-500 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
